import React, { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Toolbar,
  Typography,
  Button,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material'

import FieldEditDialog, {
  createEmptyField,
  FIELD_TYPES,
  displayFieldType,
} from './FieldEditDialog'

const verticalMenuProps = {
  MenuListProps: {
    sx: {
      '&&': {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
}

const verticalMenuItemSx = {
  '&&': {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
  },
}

const FieldsTable = ({
  fields,
  onCreateField,
  onEditField,
  onSuspendField,
  onDeleteField,
  onEditMode = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [fieldTypeFilter, setFieldTypeFilter] = useState('All')
  const [editingField, setEditingField] = useState(null) 
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState('edit')

  const handleEditClick = (field) => {
    setDialogMode('edit')
    setEditingField(field)
    setDialogOpen(true)
  }

  const handleCreateClick = () => {
    setDialogMode('create')
    setEditingField(createEmptyField())
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setEditingField(null)
    setDialogOpen(false)
    setDialogMode('edit')
  }

  const filteredFields = useMemo(() => {
    return fields.filter((field) => {
      const matchesSearch =
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (field.description || '').toLowerCase().includes(searchTerm.toLowerCase())

      const matchesType =
        fieldTypeFilter === 'All' || field.field_type === fieldTypeFilter

      return matchesSearch && matchesType
    })
  }, [fields, searchTerm, fieldTypeFilter])

  const handleSave = async (updatedField) => {
    if (dialogMode === 'create') {
      await onCreateField(updatedField)
    } else {
      await onEditField(updatedField)
    }
  }

  return (
    <div>
      <Toolbar style={{ paddingLeft: 0, paddingRight: 0, marginBottom: 8 }}>
        <Typography variant="h6" style={{ flex: 1 }}>
          Fields
        </Typography>
        {onCreateField && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            style={{ marginRight: 16 }}
          >
            Create Field
          </Button>
        )}
        <FormControl variant="outlined" size="small" style={{ marginRight: 16 }}>
          <InputLabel id="field-type-label">Field Type</InputLabel>
          <Select
            labelId="field-type-label"
            label="Field Type"
            value={fieldTypeFilter}
            onChange={(e) => setFieldTypeFilter(e.target.value)}
            style={{ width: 150 }}
            MenuProps={verticalMenuProps}
            sx={{ '&& .MuiSelect-select': { textAlign: 'left' } }}
          >
            <MenuItem value="All" sx={verticalMenuItemSx}>All</MenuItem>
            {FIELD_TYPES.map((fieldType) => (
              <MenuItem
                key={fieldType.value}
                value={fieldType.value}
                sx={verticalMenuItemSx}
              >
                {fieldType.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Toolbar>

      <TableContainer component={Paper}>
        <Table aria-label="fields table">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Field Type</TableCell>
              <TableCell>Archived?</TableCell>
              {onEditMode && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFields.length > 0 ? (
              filteredFields.map((field) => (
                <TableRow key={field.id} hover>
                  <TableCell>{field.label}</TableCell>
                  <TableCell>{field.description}</TableCell>
                  <TableCell>{displayFieldType(field.field_type)}</TableCell>
                  <TableCell>{field.archived_at ? 'Yes' : 'No'}</TableCell>

                  {onEditMode && (
                    <TableCell>
                      <IconButton
                        aria-label="edit"
                        onClick={() => handleEditClick(field)}
                      >
                        <EditIcon />
                      </IconButton>
                      <Button
                        onClick={() => onSuspendField(field)}
                        style={{ marginLeft: 8 }}
                      >
                        {field.archived_at ? 'Unsuspend' : 'Suspend'}
                      </Button>
                      <IconButton
                        aria-label="delete"
                        style={{ marginLeft: 8 }}
                        onClick={() => {
                          const confirmed = window.confirm(
                            `Delete "${field.label}"? This will remove it from all layouts and field selection lists. Saved values will be retained.`
                          )
                          if (confirmed) {
                            onDeleteField(field.id)
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={onEditMode ? 5 : 4} align="center">
                  No fields found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogOpen && editingField && (
        <FieldEditDialog
          open={dialogOpen}
          field={editingField}
          existingFields={fields}
          mode={dialogMode}
          onClose={handleDialogClose}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default FieldsTable
